import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Ban,
  BarChart3,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  ClipboardCopy,
  Crop,
  ExternalLink,
  FlaskConical,
  Image as ImageIcon,
  ImageOff,
  Images,
  Info,
  Lightbulb,
  ListOrdered,
  LoaderCircle,
  LockKeyhole,
  Maximize2,
  RefreshCw,
  RotateCcw,
  RotateCw,
  ScanSearch,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  WandSparkles,
  Wrench,
} from "lucide-react";
import type { PageContext } from "../App";
import { api, getApiError } from "../api";
import {
  Button,
  InlineError,
  MarketplaceMark,
  MarketplaceSelector,
  Modal,
  PageHeader,
  Panel,
  Select,
  StatePanel,
  StatusPill,
  formatDate,
  formatMoney,
} from "../components/ui";
import { useAsync } from "../hooks";
import { AttributeFixDialog } from "../components/AttributeFixDialog";
import { formatAttributeValue } from "../attributeFix";
import {
  sellerCentralAccountHealthUrl,
  sellerCentralInventoryUrl,
} from "../sellerCentral";
import {
  MARKETPLACE_CODES,
  MARKETPLACE_META,
  type AccountHealthSnapshot,
  type BrandLabCapabilities,
  type ContentQualityIssue,
  type ContentQualityMethodology,
  type ContentQualityResponse,
  type ContentQualitySummary,
  type FulfillmentMode,
  type ListingDraft,
  type ListingDetailResponse,
  type ListingPreview,
  type MarketplaceCode,
  type MediaAsset,
  type MediaQualityIssue,
  type MediaQualityMetric,
  type MediaState,
  type QualityEvidenceKind,
  type QualitySeverity,
} from "../types";

interface StudioAsset extends MediaAsset {
  localUrl?: string;
}

interface TransformState {
  rotate: number;
  zoom: number;
  crop: "original" | "1:1" | "4:5";
  whiteBackground: boolean;
  padding: number;
}

type ContentMediaTab = "actions" | "quality" | "studio" | "experiments";
type IssueScope = "all" | "suppressed" | "amazon" | "local";

const defaultTransform: TransformState = {
  rotate: 0,
  zoom: 100,
  crop: "1:1",
  whiteBackground: true,
  padding: 8,
};

const EMPTY_QUALITY_SUMMARY: ContentQualitySummary = {
  suppressed: 0,
  amazonErrors: 0,
  mediaProblems: 0,
  recommendations: 0,
  totalOpen: 0,
};

const SEVERITY_LABELS: Record<QualitySeverity, string> = {
  critical: "Kritisch",
  high: "Hoch",
  medium: "Mittel",
  low: "Niedrig",
};

const SEVERITY_ORDER: Record<QualitySeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const PROTECTED_MEDIA_STATES = new Set<MediaState>([
  "reserved",
  "submitted",
  "accepted",
  "live",
]);

function isProtectedMediaAsset(asset: StudioAsset | null): boolean {
  return Boolean(asset && PROTECTED_MEDIA_STATES.has(asset.state));
}

export function MediaStudioPage({ navigate, notify }: PageContext) {
  const [taskContext] = useState(() => new URLSearchParams(window.location.search));
  const focusedTask = taskContext.get("from") === "tasks";
  const requestedMarketplace = taskContext.get("marketplace") as MarketplaceCode;
  const qualityParams = {
    seller_sku: focusedTask ? taskContext.get("sku") || undefined : undefined,
    marketplace: focusedTask && MARKETPLACE_CODES.includes(requestedMarketplace) ? requestedMarketplace : undefined,
  };
  const [activeTab, setActiveTab] = useState<ContentMediaTab>(() => {
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab === "quality" || tab === "studio" || tab === "experiments"
      ? tab
      : "actions";
  });
  const [initialQualityQuery] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    return search.get("tab") === "actions" ? search.get("sku") ?? "" : "";
  });
  const quality = useAsync(() => api.contentQualityIssues({ ...qualityParams, limit: 500 }), []);
  const mediaQuality = useAsync(
    () => api.contentQualityIssues({ scope: "media", limit: 200 }),
    [],
    activeTab === "quality",
  );
  const capabilities = useAsync(
    api.brandLabCapabilities,
    [],
    activeTab === "experiments",
  );
  const [scanning, setScanning] = useState(false);
  const [activeScanJobId, setActiveScanJobId] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingMoreMedia, setLoadingMoreMedia] = useState(false);
  const [sellerSku, setSellerSku] = useState("");
  const [listing, setListing] = useState<ListingDetailResponse | null>(null);
  const [listingLoading, setListingLoading] = useState(false);
  const [listingError, setListingError] = useState<string | null>(null);
  const [countries, setCountries] = useState<MarketplaceCode[]>(["DE"]);
  const [fulfillment, setFulfillment] = useState<FulfillmentMode>("FBM");
  const [assets, setAssets] = useState<StudioAsset[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [transform, setTransform] = useState<TransformState>(defaultTransform);
  const [uploading, setUploading] = useState(false);
  const [transforming, setTransforming] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<ListingPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const assetsRef = useRef<StudioAsset[]>([]);
  const mountedRef = useRef(true);

  const selected = assets.find((asset) => asset.id === selectedId) ?? null;
  const selectedProtected = isProtectedMediaAsset(selected);
  const qualitySummary = quality.data?.summary ?? EMPTY_QUALITY_SUMMARY;

  useEffect(() => {
    assetsRef.current = assets;
  }, [assets]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      assetsRef.current.forEach((asset) => {
        if (asset.localUrl) URL.revokeObjectURL(asset.localUrl);
      });
    };
  }, []);

  const fetchListing = async (
    requestedSku: string,
    options: { assetId?: string; marketplaceCode?: MarketplaceCode } = {},
  ) => {
    const normalizedSku = requestedSku.trim();
    if (!normalizedSku) return;
    setListingLoading(true);
    setListingError(null);
    setPreview(null);
    setJobId(null);
    try {
      const result = await api.listing(normalizedSku);
      setSellerSku(normalizedSku);
      setListing(result);
      assetsRef.current.forEach((asset) => {
        if (asset.localUrl) URL.revokeObjectURL(asset.localUrl);
      });
      const persistedAssets = normalizeMediaAssets(result.mediaAssets);
      setAssets(persistedAssets);
      const preferredAsset = options.assetId
        ? persistedAssets.find(
            (asset) => asset.id === options.assetId || asset.assetId === options.assetId,
          )
        : undefined;
      setSelectedId(preferredAsset?.id ?? persistedAssets[0]?.id ?? null);
      setTransform(defaultTransform);
      if (options.marketplaceCode) setCountries([options.marketplaceCode]);
      const nextFulfillment = String(
        result.fulfillmentMode ?? result.fulfillment_mode ?? "FBM",
      ).toUpperCase();
      setFulfillment(nextFulfillment === "FBA" ? "FBA" : "FBM");
    } catch (reason) {
      setListingError(getApiError(reason));
      setListing(null);
      assetsRef.current.forEach((asset) => {
        if (asset.localUrl) URL.revokeObjectURL(asset.localUrl);
      });
      setAssets([]);
      setSelectedId(null);
    } finally {
      setListingLoading(false);
    }
  };

  const loadListing = async (event: React.FormEvent) => {
    event.preventDefault();
    await fetchListing(sellerSku);
  };

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const requestedSku = search.get("sku");
    const marketplace = search.get("marketplace");
    const requestedTab = search.get("tab");
    if (requestedSku && requestedTab !== "actions") {
      setActiveTab("studio");
      void fetchListing(requestedSku, {
        assetId: search.get("asset") ?? undefined,
        marketplaceCode: isMarketplaceCode(marketplace) ? marketplace : undefined,
      });
    }
    // The query is only read when this page is opened. Navigation remounts the
    // route, so keeping this effect one-shot avoids duplicate Amazon reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadFiles = async (files: File[]) => {
    const images = files.filter((file) => file.type.startsWith("image/"));
    if (!images.length) {
      setError("Bitte wähle JPG-, PNG- oder WEBP-Bilder aus.");
      return;
    }
    const startingOrder = assetsRef.current.length;
    const availableSlots = Math.max(0, 9 - startingOrder);
    if (availableSlots === 0) {
      setError("Für dieses Listing sind bereits 9 Bilder vorbereitet.");
      return;
    }
    const acceptedImages = images.slice(0, availableSlots);
    if (acceptedImages.length < images.length) {
      notify(
        `Es wurden nur ${acceptedImages.length} Bilder übernommen, damit das Limit von 9 nicht überschritten wird.`,
        "info",
      );
    }
    setUploading(true);
    setError(null);
    setPreview(null);
    setJobId(null);
    try {
      for (const [index, file] of acceptedImages.entries()) {
        const order = startingOrder + index;
        const response = await api.uploadMedia(file, {
          sellerSku: sellerSku.trim(),
          role: order === 0 ? "MAIN" : `PT${order}`,
        });
        const localUrl = URL.createObjectURL(file);
        const asset: StudioAsset = {
          ...response,
          id: response.id ?? response.assetId ?? `${Date.now()}-${index}`,
          assetId: response.assetId ?? response.id,
          filename: response.filename ?? file.name,
          state: response.state ?? "prepared",
          order,
          role: order === 0 ? "MAIN" : `PT${order}`,
          localUrl,
        };
        setAssets((current) => [...current, asset]);
        setSelectedId(asset.id);
      }
      notify(
        `${acceptedImages.length} Bild${acceptedImages.length === 1 ? "" : "er"} vorbereitet.`,
        "success",
      );
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setUploading(false);
    }
  };

  const applyTransform = async () => {
    if (!selected) return;
    if (isProtectedMediaAsset(selected)) {
      setError(
        "Dieses Bild ist bereits für Amazon reserviert oder übermittelt und kann nicht mehr bearbeitet werden.",
      );
      return;
    }
    setTransforming(true);
    setError(null);
    try {
      const updated = await api.transformMedia(selected.assetId ?? selected.id, {
        rotateDegrees: transform.rotate,
        crop: transform.crop,
        zoomPercent: transform.zoom,
        background: transform.whiteBackground ? "white" : "transparent",
        paddingPercent: transform.padding,
      });
      const nextPreviewUrl = updated.previewUrl ?? updated.url;
      if (nextPreviewUrl && selected.localUrl) URL.revokeObjectURL(selected.localUrl);
      setAssets((current) =>
        current.map((asset) =>
          asset.id === selected.id
            ? {
                ...asset,
                ...updated,
                id: updated.id ?? updated.assetId ?? asset.id,
                assetId: updated.assetId ?? updated.id ?? asset.assetId,
                previewUrl: nextPreviewUrl ?? asset.previewUrl,
                localUrl: nextPreviewUrl ? undefined : asset.localUrl,
                state: updated.state ?? "prepared",
              }
            : asset,
        ),
      );
      setTransform(defaultTransform);
      setPreview(null);
      setJobId(null);
      notify("Bildvariante wurde vorbereitet.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setTransforming(false);
    }
  };

  const removeAsset = async (asset: StudioAsset) => {
    if (isProtectedMediaAsset(asset)) {
      setError(
        "Reservierte, übermittelte, akzeptierte oder live geschaltete Bilder können nicht gelöscht werden.",
      );
      return;
    }
    setError(null);
    try {
      await api.deleteMedia(asset.assetId ?? asset.id);
      if (asset.localUrl) URL.revokeObjectURL(asset.localUrl);
      setAssets((current) => {
        const next = current.filter((entry) => entry.id !== asset.id);
        return next.map((entry, index) => ({
          ...entry,
          order: index,
          role: index === 0 ? "MAIN" : `PT${index}`,
        }));
      });
      if (selectedId === asset.id) {
        setSelectedId(assets.find((entry) => entry.id !== asset.id)?.id ?? null);
      }
      setPreview(null);
      setJobId(null);
      notify("Bild wurde aus dem Entwurf entfernt.", "success");
    } catch (reason) {
      setError(getApiError(reason));
    }
  };

  const move = (assetId: string, direction: -1 | 1) => {
    setAssets((current) => {
      const index = current.findIndex((asset) => asset.id === assetId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((asset, position) => ({
        ...asset,
        order: position,
        role: position === 0 ? "MAIN" : `PT${position}`,
      }));
    });
    setPreview(null);
    setJobId(null);
  };

  const makeDraft = (): ListingDraft => ({
    mode: "MATCH_EXISTING_ASIN",
    sellerSku: sellerSku.trim(),
    asin: readString(listing, "asin"),
    productType: readString(listing, "productType") ?? readString(listing, "product_type"),
    condition: "new_new",
    targetMarketplaces: countries,
    fulfillmentMode: fulfillment,
    startPrices: {},
    shippingTemplates: {},
    attributes: {},
    mediaLocators: assets.map((asset, index) => ({
      assetId: asset.assetId ?? asset.id,
      role: index === 0 ? "MAIN" : `PT${index}`,
      order: index,
    })),
  });

  const createPreview = async () => {
    setPreviewing(true);
    setError(null);
    try {
      setPreview(await api.previewListing(makeDraft()));
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    if (!preview) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.submitListing({
        previewId: preview.previewId,
        confirmationToken: preview.confirmationToken,
        idempotencyKey:
          globalThis.crypto?.randomUUID?.() ?? `media-${Date.now()}-${sellerSku}`,
      });
      setJobId(result.jobId);
      notify(`Medien-Job ${result.jobId} wurde eingereiht.`, "success");
    } catch (reason) {
      setError(getApiError(reason));
    } finally {
      setSubmitting(false);
    }
  };

  const changeTab = (tab: ContentMediaTab) => {
    setActiveTab(tab);
    const search = new URLSearchParams();
    search.set("tab", tab);
    if (tab === "studio" && sellerSku.trim()) search.set("sku", sellerSku.trim());
    window.history.replaceState({}, "", `/media?${search.toString()}`);
  };

  const scanContentQuality = async () => {
    setScanning(true);
    try {
      const result = await api.scanContentQuality({ limit: 500 });
      notify(
        result.reused
          ? `Qualitätsprüfung läuft bereits · Job ${result.jobId}.`
          : `Lesende Content-Prüfung gestartet · Job ${result.jobId}. Es wird nichts an Amazon geändert.`,
        result.reused ? "info" : "success",
      );
      setActiveScanJobId(result.jobId);
      void monitorQualityJob(result.jobId, "Qualitätsprüfung", true);
    } catch (reason) {
      notify(getApiError(reason), "error");
    } finally {
      setScanning(false);
    }
  };

  const monitorQualityJob = async (
    jobId: string,
    label: string,
    trackAsScan: boolean,
  ) => {
    try {
      for (let attempt = 0; attempt < 360 && mountedRef.current; attempt += 1) {
        const job = await api.job(jobId);
        if (job.status === "succeeded") {
          await quality.reload();
          await mediaQuality.reload();
          if (mountedRef.current) {
            notify(job.message || `${label} abgeschlossen.`, "success");
          }
          return;
        }
        if (job.status === "failed" || job.status === "cancelled") {
          await quality.reload();
          await mediaQuality.reload();
          if (mountedRef.current) {
            notify(
              job.message || `${label} wurde nicht erfolgreich abgeschlossen.`,
              "error",
            );
          }
          return;
        }
        await wait(5_000);
      }
      if (mountedRef.current) {
        notify(
          `${label} läuft weiter. Den aktuellen Stand findest du unter Jobs & Audit.`,
          "info",
        );
      }
    } catch (reason) {
      if (mountedRef.current) notify(getApiError(reason), "error");
    } finally {
      if (trackAsScan && mountedRef.current) {
        setActiveScanJobId((current) => (current === jobId ? null : current));
      }
    }
  };

  const loadMoreIssues = async () => {
    const cursor = quality.data?.nextCursor;
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await api.contentQualityIssues({ ...qualityParams, cursor });
      quality.setData((current) =>
        current
          ? {
              ...next,
              items: [
                ...current.items,
                ...next.items.filter(
                  (item) => !current.items.some((existing) => existing.id === item.id),
                ),
              ],
              summary: current.summary,
            }
          : next,
      );
    } catch (reason) {
      notify(getApiError(reason), "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const loadMoreMediaIssues = async () => {
    const cursor = mediaQuality.data?.nextCursor;
    if (!cursor || loadingMoreMedia) return;
    setLoadingMoreMedia(true);
    try {
      const next = await api.contentQualityIssues({
        scope: "media",
        limit: 200,
        cursor,
      });
      mediaQuality.setData((current) =>
        current
          ? {
              ...next,
              items: [
                ...current.items,
                ...next.items.filter(
                  (item) =>
                    !current.items.some((existing) => existing.id === item.id),
                ),
              ],
              summary: current.summary,
            }
          : next,
      );
    } catch (reason) {
      notify(getApiError(reason), "error");
    } finally {
      setLoadingMoreMedia(false);
    }
  };

  const openIssueInStudio = async (issue: ContentQualityIssue) => {
    const preferredAssetId =
      issue.affectedMedia.find((item) => item.assetId)?.assetId ?? undefined;
    setActiveTab("studio");
    const search = new URLSearchParams({
      tab: "studio",
      sku: issue.sellerSku,
      marketplace: issue.marketplaceCode,
      issue: issue.id,
    });
    if (preferredAssetId) search.set("asset", preferredAssetId);
    window.history.replaceState({}, "", `/media?${search.toString()}`);
    await fetchListing(issue.sellerSku, {
      assetId: preferredAssetId,
      marketplaceCode: issue.marketplaceCode,
    });
  };

  const refreshIssue = async (issue: ContentQualityIssue) => {
    try {
      const result = await api.verifyListing(issue.sellerSku);
      notify(
        result.jobId
          ? `Live-Prüfung gestartet · Job ${result.jobId}`
          : "Live-Prüfung wurde gestartet.",
        "success",
      );
      if (result.jobId) {
        void monitorQualityJob(result.jobId, "Live-Prüfung", false);
      }
    } catch (reason) {
      notify(getApiError(reason), "error");
    }
  };

  const handleIssueAction = async (issue: ContentQualityIssue) => {
    if (issue.fixAction.kind === "open_media_studio") {
      await openIssueInStudio(issue);
      return;
    }
    if (issue.fixAction.kind === "refresh_listing") {
      await refreshIssue(issue);
      return;
    }
    if (issue.fixAction.kind === "change_price") {
      navigate("listings", {
        sku: issue.sellerSku,
        marketplace: issue.marketplaceCode,
        issue: issue.id,
        view: "issues",
        action: "price",
        ...(focusedTask ? { from: "tasks" } : {}),
      });
      return;
    }
    if (issue.fixAction.kind === "open_seller_central") {
      window.open(
        sellerCentralInventoryUrl(issue.marketplaceCode, issue),
        "_blank",
        "noopener,noreferrer",
      );
    }
  };

  return (
    <div className="media-page">
      <PageHeader
        eyebrow="Content-Qualität"
        title={focusedTask ? "Aufgabe bearbeiten" : "Probleme & Bilder"}
        description={focusedTask ? `${taskContext.get("sku")} · ${taskContext.get("marketplace")}` : "Ein Problem auswählen und den nächsten Schritt ausführen."}
        actions={
          <Button
            variant="secondary"
            loading={scanning}
            disabled={Boolean(activeScanJobId)}
            onClick={scanContentQuality}
            icon={<ScanSearch size={16} />}
          >
            {activeScanJobId ? "Prüfung läuft" : "Qualität neu prüfen"}
          </Button>
        }
      />

      {focusedTask && <Button variant="ghost" onClick={() => navigate("home", taskContext.get("work") === "opportunities" ? { view: "opportunities" } : undefined)}>Zurück zu meinen Aufgaben</Button>}
      {!focusedTask && <ContentMediaTabs
        active={activeTab}
        summary={qualitySummary}
        onChange={changeTab}
      />}

      {activeTab === "actions" && (
        <IssueInbox
          response={quality.data}
          loading={quality.loading}
          error={quality.error}
          loadingMore={loadingMore}
          initialQuery={initialQualityQuery}
          initialSelectedId={taskContext.get("issue")}
          initialMarketplace={taskContext.get("marketplace")}
          focused={focusedTask}
          onConfirmed={focusedTask ? async () => { navigate("home", { view: "waiting" }); } : undefined}
          onRetry={quality.reload}
          onLoadMore={loadMoreIssues}
          onAction={handleIssueAction}
          notify={notify}
        />
      )}

      {activeTab === "quality" && (
        <ImageQualityOverview
          response={mediaQuality.data}
          loading={mediaQuality.loading}
          error={mediaQuality.error}
          loadingMore={loadingMoreMedia}
          onRetry={mediaQuality.reload}
          onLoadMore={loadMoreMediaIssues}
          onOpenStudio={openIssueInStudio}
        />
      )}

      {activeTab === "experiments" && (
        <LockedExperimentLab
          capabilities={capabilities.data}
          loading={capabilities.loading}
          unavailable={Boolean(capabilities.error)}
        />
      )}

      {activeTab === "studio" && (
        <>
          <Panel className="media-context-panel">
        <div className="media-context-panel__copy">
          <span><Images size={20} /></span>
          <div><strong>Listing auswählen</strong><p>Die SKU verbindet die vorbereiteten Medien mit dem richtigen Angebot.</p></div>
        </div>
        <form className="media-listing-search" onSubmit={loadListing}>
          <Search size={17} />
          <input
            value={sellerSku}
            onChange={(event) => setSellerSku(event.target.value)}
            placeholder="Seller-SKU eingeben …"
            aria-label="Seller-SKU"
          />
          <Button type="submit" loading={listingLoading} disabled={!sellerSku.trim()}>
            Laden
          </Button>
        </form>
        {listing && (
          <div className="media-listing-result">
            <CheckCircle2 size={17} />
            <div>
              <strong>{readString(listing, "title") ?? sellerSku}</strong>
              <span>
                {readString(listing, "asin") ? `ASIN ${readString(listing, "asin")} · ` : ""}
                {fulfillment}
              </span>
            </div>
          </div>
        )}
        {listingError && <InlineError message={listingError} />}
          </Panel>

      <div className="media-status-guide">
        {(["prepared", "reserved", "submitted", "accepted", "live"] as const).map((state, index) => (
          <div key={state}>
            <StatusPill status={state} />
            {index < 4 && <span className="media-status-guide__line" />}
          </div>
        ))}
        <p>
          <Info size={15} /> Akzeptiert heißt: Amazon hat die Übermittlung angenommen. Erst
          der spätere Live-Abgleich bestätigt das sichtbare Bild.
        </p>
      </div>

      {error && <InlineError message={error} />}

      {!listing ? (
        <Panel className="media-empty">
          <StatePanel
            type="empty"
            title="Zuerst ein Listing laden"
            description="Gib oben die Seller-SKU ein. Danach kannst du Bilder hochladen und eine sichere Medienänderung vorbereiten."
          />
        </Panel>
      ) : (
        <>
          <div className="media-workspace">
            <Panel className="asset-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Bildreihenfolge</p>
                  <h2>Assets</h2>
                </div>
                <span>{assets.length}/9</span>
              </div>

              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={(event) => {
                  void uploadFiles(Array.from(event.target.files ?? []));
                  event.target.value = "";
                }}
              />
              <button
                type="button"
                className={`upload-dropzone ${dragging ? "is-dragging" : ""} ${
                  assets.length >= 9 ? "is-full" : ""
                }`}
                disabled={uploading || assets.length >= 9}
                onClick={() => fileInput.current?.click()}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  void uploadFiles(Array.from(event.dataTransfer.files));
                }}
              >
                {uploading ? (
                  <LoaderCircle className="spin" size={22} />
                ) : assets.length >= 9 ? (
                  <CheckCircle2 size={22} />
                ) : (
                  <Upload size={22} />
                )}
                <strong>
                  {uploading
                    ? "Bilder werden vorbereitet …"
                    : assets.length >= 9
                      ? "Bildlimit erreicht"
                      : "Bilder hochladen"}
                </strong>
                <span>
                  {assets.length >= 9
                    ? "Entferne zuerst ein vorbereitetes Bild."
                    : "JPG, PNG oder WEBP · ideal ab 1.600 × 1.600 px"}
                </span>
              </button>

              {assets.length === 0 ? (
                <div className="asset-list-empty">
                  <ImageIcon size={22} />
                  <p>Noch keine Bilder im Entwurf.</p>
                </div>
              ) : (
                <div className="asset-list">
                  {assets.map((asset, index) => (
                    <div
                      key={asset.id}
                      className={`asset-item ${selectedId === asset.id ? "is-selected" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setSelectedId(asset.id);
                        setTransform(defaultTransform);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedId(asset.id);
                          setTransform(defaultTransform);
                        }
                      }}
                    >
                      <span className="asset-list__thumb">
                        {asset.previewUrl || asset.url || asset.localUrl ? (
                          <img src={asset.previewUrl ?? asset.url ?? asset.localUrl} alt="" />
                        ) : (
                          <ImageIcon size={20} />
                        )}
                        <small>{index === 0 ? "MAIN" : `PT${index}`}</small>
                      </span>
                      <span className="asset-list__copy">
                        <strong>{asset.filename ?? `Bild ${index + 1}`}</strong>
                        <StatusPill status={asset.state} />
                      </span>
                      <span className="asset-list__moves">
                        <button
                          type="button"
                          aria-label="Bild nach oben"
                          disabled={index === 0}
                          onClick={(event) => {
                            event.stopPropagation();
                            move(asset.id, -1);
                          }}
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          type="button"
                          aria-label="Bild nach unten"
                          disabled={index === assets.length - 1}
                          onClick={(event) => {
                            event.stopPropagation();
                            move(asset.id, 1);
                          }}
                        >
                          <ArrowDown size={14} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel className="media-canvas-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Arbeitsfläche</p>
                  <h2>{selected?.filename ?? "Bild auswählen"}</h2>
                </div>
                {selected && <StatusPill status={selected.state} />}
              </div>
              <div
                className={`media-canvas ${
                  transform.whiteBackground ? "media-canvas--white" : "media-canvas--checker"
                } media-canvas--${transform.crop.replace(":", "-")}`}
              >
                {selected ? (
                  selected.previewUrl || selected.url || selected.localUrl ? (
                    <img
                      src={selected.previewUrl ?? selected.url ?? selected.localUrl}
                      alt="Vorschau des ausgewählten Amazon-Bildes"
                      style={{
                        transform: `rotate(${transform.rotate}deg) scale(${transform.zoom / 100})`,
                        padding: `${transform.padding}%`,
                      }}
                    />
                  ) : (
                    <div className="media-canvas__placeholder"><ImageIcon size={35} /><span>Server-Vorschau wird erzeugt</span></div>
                  )
                ) : (
                  <div className="media-canvas__placeholder"><Crop size={35} /><span>Wähle links ein Bild aus</span></div>
                )}
                <span className="media-safe-area">Amazon safe area</span>
              </div>
              {selected && (
                <div className="media-canvas-meta">
                  <span>{selected.width && selected.height ? `${selected.width} × ${selected.height} px` : "Auflösung wird geprüft"}</span>
                  <span>{assets.findIndex((asset) => asset.id === selected.id) === 0 ? "Hauptbild" : "Zusatzbild"}</span>
                </div>
              )}
            </Panel>

            <Panel className="media-tools-panel">
              <div className="panel-heading">
                <div>
                  <p className="eyebrow">Bearbeitung</p>
                  <h2>Werkzeuge</h2>
                </div>
                <WandSparkles size={20} />
              </div>
              {!selected ? (
                <StatePanel type="empty" compact title="Kein Bild gewählt" description="Wähle links ein Asset aus." />
              ) : (
                <>
                  <AssetQualitySummary asset={selected} />
                  {selectedProtected && (
                    <div className="media-protected-note">
                      <ShieldCheck size={17} />
                      <p>
                        <strong>Bild ist gegen Änderungen geschützt</strong>
                        <span>
                          Status „{selected.state}“: Transformation und Löschen sind nicht
                          mehr möglich. Lade für eine Änderung ein neues Asset hoch.
                        </span>
                      </p>
                    </div>
                  )}
                  <div className="tool-section">
                    <span className="tool-section__label"><RotateCw size={16} /> Drehung</span>
                    <div className="tool-buttons">
                      <button
                        type="button"
                        disabled={selectedProtected}
                        onClick={() => setTransform((current) => ({ ...current, rotate: current.rotate - 90 }))}
                      >
                        <RotateCcw size={17} /> −90°
                      </button>
                      <button
                        type="button"
                        disabled={selectedProtected}
                        onClick={() => setTransform((current) => ({ ...current, rotate: current.rotate + 90 }))}
                      >
                        <RotateCw size={17} /> +90°
                      </button>
                    </div>
                  </div>
                  <div className="tool-section">
                    <span className="tool-section__label"><Crop size={16} /> Zuschnitt</span>
                    <div className="segmented segmented--three">
                      {(["original", "1:1", "4:5"] as const).map((value) => (
                        <button
                          type="button"
                          disabled={selectedProtected}
                          className={transform.crop === value ? "is-active" : ""}
                          onClick={() => setTransform((current) => ({ ...current, crop: value }))}
                          key={value}
                        >
                          {value === "original" ? "Original" : value}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="tool-section">
                    <label className="range-field">
                      <span><Maximize2 size={16} /> Skalierung <strong>{transform.zoom}%</strong></span>
                      <input
                        type="range"
                        min="70"
                        max="140"
                        value={transform.zoom}
                        disabled={selectedProtected}
                        onChange={(event) =>
                          setTransform((current) => ({ ...current, zoom: Number(event.target.value) }))
                        }
                      />
                    </label>
                  </div>
                  <div className="tool-section">
                    <label className="range-field">
                      <span><Sparkles size={16} /> Rand <strong>{transform.padding}%</strong></span>
                      <input
                        type="range"
                        min="0"
                        max="20"
                        value={transform.padding}
                        disabled={selectedProtected}
                        onChange={(event) =>
                          setTransform((current) => ({ ...current, padding: Number(event.target.value) }))
                        }
                      />
                    </label>
                  </div>
                  <label className="toggle-row">
                    <span className="toggle">
                      <input
                        type="checkbox"
                        checked={transform.whiteBackground}
                        disabled={selectedProtected}
                        onChange={(event) =>
                          setTransform((current) => ({
                            ...current,
                            whiteBackground: event.target.checked,
                          }))
                        }
                      />
                      <span />
                    </span>
                    <span><strong>Weißer Hintergrund</strong><small>Für Amazon-Hauptbild empfohlen</small></span>
                  </label>
                  <div className="media-tool-actions">
                    <Button
                      variant="secondary"
                      loading={transforming}
                      disabled={selectedProtected}
                      onClick={applyTransform}
                      icon={<WandSparkles size={16} />}
                    >
                      Variante vorbereiten
                    </Button>
                    <Button
                      variant="danger"
                      disabled={selectedProtected}
                      onClick={() => void removeAsset(selected)}
                      icon={<Trash2 size={16} />}
                    >
                      Entfernen
                    </Button>
                  </div>
                </>
              )}
            </Panel>
          </div>

          <Panel className="media-submit-panel">
            <div className="media-submit-panel__heading">
              <div>
                <p className="eyebrow">Amazon-Übermittlung</p>
                <h2>Medienänderung prüfen</h2>
                <p>{assets.length} vorbereitete Assets · kein Bestands- oder Preiseingriff</p>
              </div>
              <MarketplaceSelector
                value={countries}
                multiple
                label="Zielländer"
                onChange={(value) => {
                  setCountries(value as MarketplaceCode[]);
                  setPreview(null);
                }}
              />
            </div>

            {jobId ? (
              <div className="media-job-success">
                <span><Check size={20} /></span>
                <div>
                  <strong>Medien-Job wurde eingereiht</strong>
                  <p>Job {jobId}. Nach Annahme folgt eine separate Live-Prüfung je Marketplace.</p>
                </div>
                <StatusPill status="queued" />
              </div>
            ) : preview ? (
              <div className="media-preview">
                <div className="media-preview__countries">
                  {preview.marketplaces.map((item) => (
                    <div key={item.code}>
                      <MarketplaceMark code={item.code} decorative />
                      <div>
                        <strong>{MARKETPLACE_META[item.code].label}</strong>
                        <small>{item.diff.length} Änderungen</small>
                      </div>
                      <StatusPill status={item.errors.length ? "error" : item.warnings.length ? "warning" : "success"} />
                    </div>
                  ))}
                </div>
                <div className="availability-rule">
                  <ShieldCheck size={17} />
                  <span>Diese Vorschau enthält ausschließlich Medien-Locators. Bestehende Mengen und Preise fehlen absichtlich im Request.</span>
                </div>
                <div className="media-preview__actions">
                  <Button variant="ghost" onClick={() => setPreview(null)}>Vorschau verwerfen</Button>
                  <Button
                    loading={submitting}
                    disabled={!preview.canSubmit}
                    onClick={submit}
                    icon={<Send size={16} />}
                  >
                    Medien an Amazon senden
                  </Button>
                </div>
              </div>
            ) : (
              <div className="media-submit-panel__footer">
                <div className="amazon-media-note">
                  <Info size={17} />
                  <span>Amazon kann Bilder akzeptieren, ohne sie sofort sichtbar auszuspielen.</span>
                </div>
                <Button
                  disabled={!assets.length || !countries.length}
                  loading={previewing}
                  onClick={createPreview}
                  icon={<ShieldCheck size={16} />}
                >
                  Sichere Vorschau erzeugen
                </Button>
              </div>
            )}
          </Panel>
        </>
          )}
        </>
      )}
    </div>
  );
}

function ContentMediaTabs({
  active,
  summary,
  onChange,
}: {
  active: ContentMediaTab;
  summary: ContentQualitySummary;
  onChange: (tab: ContentMediaTab) => void;
}) {
  const tabs: Array<{
    id: ContentMediaTab;
    label: string;
    icon: React.ReactNode;
    badge?: number | string;
  }> = [
    {
      id: "actions",
      label: "Handlungsbedarf",
      icon: <CircleAlert size={17} />,
      badge: summary.totalOpen,
    },
    {
      id: "quality",
      label: "Bildqualität",
      icon: <BarChart3 size={17} />,
      badge: summary.mediaProblems,
    },
    { id: "studio", label: "Media Studio", icon: <Images size={17} /> },
    {
      id: "experiments",
      label: "Experimente",
      icon: <FlaskConical size={17} />,
      badge: "Gesperrt",
    },
  ];

  return (
    <div className="content-media-tabs" role="tablist" aria-label="Content und Media">
      {tabs.map((tab) => (
        <button
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          className={active === tab.id ? "is-active" : ""}
          onClick={() => onChange(tab.id)}
          key={tab.id}
        >
          {tab.icon}
          <span>{tab.label}</span>
          {tab.badge !== undefined && <small>{tab.badge}</small>}
        </button>
      ))}
    </div>
  );
}

function QualitySummaryStrip({ summary }: { summary: ContentQualitySummary }) {
  const items = [
    {
      label: "Unterdrückt",
      value: summary.suppressed,
      detail: "Amazon-Status",
      tone: "danger",
      icon: <Ban size={18} />,
    },
    {
      label: "Amazon-Fehler",
      value: summary.amazonErrors,
      detail: "API-bestätigt",
      tone: "warning",
      icon: <AlertTriangle size={18} />,
    },
    {
      label: "Bildprüfung",
      value: summary.mediaProblems,
      detail: "offene Hinweise",
      tone: "media",
      icon: <ImageOff size={18} />,
    },
    {
      label: "Empfehlungen",
      value: summary.recommendations,
      detail: "lokale Prüfung",
      tone: "neutral",
      icon: <Lightbulb size={18} />,
    },
  ];

  return (
    <section className="quality-summary-strip" aria-label="Content-Qualität Zusammenfassung">
      {items.map((item) => (
        <article className={`quality-summary-card quality-summary-card--${item.tone}`} key={item.label}>
          <span>{item.icon}</span>
          <div>
            <strong>{item.value}</strong>
            <p>{item.label}</p>
            <small>{item.detail}</small>
          </div>
        </article>
      ))}
    </section>
  );
}

function AccountHealthOverview({
  snapshot,
}: {
  snapshot?: AccountHealthSnapshot;
}) {
  if (!snapshot) return null;
  const actionable = snapshot.metrics.filter(
    (metric) =>
      metric.count > 0 ||
      !["GOOD", "HEALTHY", "NORMAL"].includes(String(metric.status).toUpperCase()),
  );
  const marketplace =
    snapshot.accountStatuses.find((item) => item.marketplaceCode)?.marketplaceCode ??
    snapshot.accountHealthRatings.find((item) => item.marketplaceCode)?.marketplaceCode ??
    "DE";
  const accountStatus = snapshot.accountStatuses.find(
    (item) => item.marketplaceCode === marketplace,
  );
  const rating = snapshot.accountHealthRatings.find(
    (item) => item.marketplaceCode === marketplace,
  );
  const openDashboard = (targetMarketplace: MarketplaceCode = marketplace) =>
    window.open(
      sellerCentralAccountHealthUrl(targetMarketplace),
      "_blank",
      "noopener,noreferrer",
    );

  return (
    <Panel className="account-health-panel">
      <header className="account-health-header">
        <div>
          <span><ShieldCheck size={19} /></span>
          <div>
            <p className="eyebrow">Seller Central · Verkäuferleistung</p>
            <h2>Kontozustand und Richtlinienprobleme</h2>
            <p>
              Offizielle Fallzahlen aus Amazons Verkäuferleistungsbericht, getrennt
              von artikelbezogenen Listings-Issues.
            </p>
          </div>
        </div>
        <div className="account-health-header__status">
          <MarketplaceMark code={marketplace} />
          {accountStatus && <strong>Konto: {accountStatus.status}</strong>}
          {rating && <span>AHR: {rating.status}</span>}
          <small>
            {snapshot.lastConfirmedAt
              ? `Amazon-Stand ${formatDate(snapshot.lastConfirmedAt)}`
              : "Noch kein Amazon-Bericht"}
          </small>
        </div>
      </header>

      {snapshot.status !== "available" ? (
        <div className="account-health-unavailable">
          <Info size={17} />
          <div>
            <strong>Account-Health-Bericht noch nicht verfügbar</strong>
            <p>{snapshot.message || snapshot.limitation}</p>
          </div>
          <Button variant="secondary" icon={<ExternalLink size={15} />} onClick={() => openDashboard()}>
            Kontozustand öffnen
          </Button>
        </div>
      ) : actionable.length ? (
        <div className="account-health-list">
          {actionable.map((metric) => (
            <article key={metric.key}>
              <span className={metric.count ? "has-defects" : "has-status"}>
                {metric.count}
              </span>
              <div>
                <strong>Was ist falsch? {metric.label}</strong>
                <div className="account-health-marketplaces" aria-label="Betroffene Marktplätze">
                  <small>Betroffen:</small>
                  {(metric.marketplaces.length ? metric.marketplaces : [marketplace]).map(
                    (code) => (
                      <span key={code}>
                        <MarketplaceMark code={code} />
                        {MARKETPLACE_META[code].label} ({code})
                      </span>
                    ),
                  )}
                </div>
                <p>
                  Warum? Amazon meldet für den Kontozustand den Status {metric.status}
                  {metric.count ? ` und ${metric.count} offene Fälle` : ""}. Der Bericht
                  nennt dabei keine betroffenen ASINs oder SKUs.
                </p>
                <small>
                  Lösung: Fälle im Seller-Central-Kontozustand öffnen, Nachweise oder
                  Korrekturen einreichen und danach den Bericht neu abrufen.
                </small>
              </div>
              <Button
                variant="ghost"
                icon={<ExternalLink size={14} />}
                onClick={() => openDashboard(metric.marketplaces[0] ?? marketplace)}
              >
                Fälle prüfen
              </Button>
            </article>
          ))}
        </div>
      ) : (
        <div className="account-health-empty">
          <CheckCircle2 size={18} />
          <span>
            Im letzten Bericht sind keine Account-Health-Warnungen mit Fallzahl
            enthalten.
          </span>
          <Button variant="ghost" icon={<ExternalLink size={14} />} onClick={() => openDashboard()}>
            Kontozustand öffnen
          </Button>
        </div>
      )}

      <footer>
        <Info size={14} /> {snapshot.limitation}
      </footer>
    </Panel>
  );
}

export function IssueInbox({
  response,
  loading,
  error,
  loadingMore,
  initialQuery,
  initialSelectedId = null,
  initialMarketplace = null,
  focused = false,
  onConfirmed,
  onRetry,
  onLoadMore,
  onAction,
  notify,
}: {
  response: ContentQualityResponse | null;
  loading: boolean;
  error: string | null;
  loadingMore: boolean;
  initialQuery: string;
  initialSelectedId?: string | null;
  initialMarketplace?: string | null;
  focused?: boolean;
  onConfirmed?: () => Promise<void>;
  onRetry: () => Promise<void>;
  onLoadMore: () => Promise<void>;
  onAction: (issue: ContentQualityIssue) => Promise<void>;
  notify: PageContext["notify"];
}) {
  const [scope, setScope] = useState<IssueScope>("all");
  const [query, setQuery] = useState(initialQuery);
  const [marketplace, setMarketplace] = useState<MarketplaceCode | "">(
    MARKETPLACE_CODES.includes(initialMarketplace as MarketplaceCode) ? initialMarketplace as MarketplaceCode : "",
  );
  const [selectedId, setSelectedId] = useState<string | null>(initialSelectedId);
  const items = response?.items ?? [];

  const visibleIssues = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("de");
    return [...items]
      .filter((issue) => {
        if (
          scope === "suppressed" &&
          issue.category !== "suppression" &&
          issue.listingStatus !== "suppressed"
        ) {
          return false;
        }
        if (scope === "amazon" && !isAmazonEvidence(issue.evidence.kind)) return false;
        if (scope === "local" && isAmazonEvidence(issue.evidence.kind)) return false;
        if (marketplace && issue.marketplaceCode !== marketplace) return false;
        if (!normalizedQuery) return true;
        return [
          issue.sellerSku,
          issue.asin,
          issue.title,
          issue.code,
          issue.message,
        ].some((value) => String(value ?? "").toLocaleLowerCase("de").includes(normalizedQuery));
      })
      .sort((left, right) => {
        const leftSuppressed =
          left.category === "suppression" || left.listingStatus === "suppressed" ? 0 : 1;
        const rightSuppressed =
          right.category === "suppression" || right.listingStatus === "suppressed" ? 0 : 1;
        return (
          leftSuppressed - rightSuppressed ||
          SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity] ||
          String(right.lastConfirmedAt ?? "").localeCompare(String(left.lastConfirmedAt ?? ""))
        );
      });
  }, [items, marketplace, query, scope]);

  useEffect(() => {
    if (!response) return;
    if (!visibleIssues.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !visibleIssues.some((issue) => issue.id === selectedId)) {
      setSelectedId(visibleIssues[0].id);
    }
  }, [response, selectedId, visibleIssues]);

  const selected =
    visibleIssues.find((issue) => issue.id === selectedId) ?? visibleIssues[0] ?? null;
  const methodology = methodologyText(response?.methodology);

  if (focused) {
    const requestedMissing = initialSelectedId && !items.some(item => item.id === initialSelectedId);
    return <div className="focused-task">
      {error && <InlineError message={error} onRetry={() => void onRetry()} />}
      {loading && !response ? <StatePanel type="loading" title="Aufgabe wird geladen" />
        : response && (requestedMissing || !selected) ? <StatePanel type="empty"
          title="Diese Meldung ist nicht mehr in den offenen Aufgaben"
          description="Sie kann bereits auf eine Nachprüfung warten oder durch einen Abgleich geändert worden sein. Die Aufgabenübersicht zeigt den aktuellen Stand."
          action={response.nextCursor ? <Button onClick={() => void onLoadMore()} loading={loadingMore}>Weitere Meldungen laden</Button> : undefined} />
        : selected && <>
          {visibleIssues.length > 1 && <details className="task-related"><summary>{visibleIssues.length} Meldungen zu diesem Artikel und Land</summary>
            <div>{visibleIssues.map(issue => <button type="button" key={issue.id} aria-pressed={selected.id === issue.id} onClick={() => setSelectedId(issue.id)}>{issue.fieldGuidance?.title || issue.playbook?.title || issue.message}</button>)}</div>
          </details>}
          <IssueDetailPanel key={selected.id} issue={selected} onAction={onAction} notify={notify} onConfirmed={onConfirmed ?? onRetry} />
        </>}
    </div>;
  }

  return (
    <div className="quality-inbox-page">
      <details className="task-technical"><summary>Kennzahlen und Kontozustand</summary>
      <QualitySummaryStrip summary={response?.summary ?? EMPTY_QUALITY_SUMMARY} />
      <AccountHealthOverview snapshot={response?.accountHealth} />
      </details>

      <Panel className="quality-inbox-panel">
        <div className="quality-inbox-heading">
          <div>
            <p className="eyebrow">Priorisierte Aufgaben</p>
            <h2>Fehler-Inbox</h2>
            <p>
              Unterdrückte Listings zuerst. Jede Aufgabe bleibt an SKU und Marketplace
              gebunden.
            </p>
          </div>
          <span>
            <strong>{response?.total ?? items.length}</strong>
            offene Aufgaben
            <small>
              {response?.summary.lastScanAt
                ? `Letzte Prüfung ${formatDate(response.summary.lastScanAt)}`
                : "Noch keine Amazon-Qualitätsprüfung"}
            </small>
          </span>
        </div>

        {Boolean(response?.waitingCount) && (
          <div className="quality-methodology-note" role="status">
            <Info size={17} />
            <p>{response?.waitingCount} bestätigte Preisfehler warten auf ihre Prüfung nach 24 Stunden. Fortbestehende Fehler erscheinen anschließend wieder hier.</p>
          </div>
        )}
        <details className="task-technical"><summary>Quellen und Einordnung der Meldungen</summary>
        <div className="quality-methodology-note">
          <ShieldCheck size={17} />
          <div>
            <strong>Amazon-Aussage und lokale Prüfung bleiben getrennt</strong>
            <p>
              {methodology ??
                "Amazon-Status und API-Issues werden wörtlich ausgewiesen. Technische Messungen und Empfehlungen sind interne Prüfungen."}
            </p>
          </div>
          <div className="quality-methodology-note__legend">
            <EvidenceBadge kind="amazon_confirmed" source="Amazon" />
            <EvidenceBadge kind="local_measured" source="Playzeug" />
          </div>
        </div>
        </details>

        <div className="quality-inbox-toolbar">
          <div className="quality-scope-tabs" role="group" aria-label="Aufgabentyp filtern">
            {(
              [
                ["all", "Alle"],
                ["suppressed", "Unterdrückt"],
                ["amazon", "Amazon"],
                ["local", "Lokal geprüft"],
              ] as Array<[IssueScope, string]>
            ).map(([value, label]) => (
              <button
                type="button"
                className={scope === value ? "is-active" : ""}
                aria-pressed={scope === value}
                onClick={() => setScope(value)}
                key={value}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="quality-search">
            <Search size={17} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="SKU, ASIN, Titel oder Fehlercode …"
              aria-label="Aufgaben durchsuchen"
            />
          </label>
          <Select
            value={marketplace}
            aria-label="Marketplace filtern"
            onChange={(event) =>
              setMarketplace(event.target.value as MarketplaceCode | "")
            }
          >
            <option value="">Alle Marketplaces</option>
            {MARKETPLACE_CODES.map((code) => (
              <option value={code} key={code}>
                {MARKETPLACE_META[code].label} ({code})
              </option>
            ))}
          </Select>
        </div>

        {error && !response ? (
          <InlineError message={error} onRetry={() => void onRetry()} />
        ) : loading && !response ? (
          <StatePanel
            type="loading"
            title="Content-Qualität wird geladen"
            description="Die zuletzt synchronisierten Amazon-Befunde werden aufbereitet."
          />
        ) : visibleIssues.length === 0 ? (
          <StatePanel
            type="empty"
            title="Keine offenen Aufgaben für diesen Filter"
            description={
              response?.nextCursor
                ? "Im geladenen Ausschnitt gibt es keinen Treffer. Lade weitere Aufgaben oder passe den Filter an."
                : "Passe Suche oder Filter an. Ein grüner Zustand wird erst nach dem letzten Live-Abgleich angenommen."
            }
            action={
              response?.nextCursor ? (
                <Button
                  variant="secondary"
                  loading={loadingMore}
                  onClick={() => void onLoadMore()}
                >
                  Weitere Aufgaben laden
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="quality-master-detail">
            <div className="quality-issue-list" aria-label="Offene Content-Aufgaben">
              {visibleIssues.map((issue) => (
                <button
                  type="button"
                  className={`quality-issue-row ${
                    selected?.id === issue.id ? "is-selected" : ""
                  } ${
                    issue.category === "suppression" ||
                    issue.listingStatus === "suppressed"
                      ? "is-suppressed"
                      : ""
                  }`}
                  onClick={() => setSelectedId(issue.id)}
                  key={issue.id}
                >
                  <span className="quality-issue-row__image">
                    {issue.imageUrl || issue.affectedMedia[0]?.url ? (
                      <img
                        src={issue.imageUrl || issue.affectedMedia[0]?.url || undefined}
                        alt=""
                        loading="lazy"
                      />
                    ) : (
                      <ImageOff size={20} />
                    )}
                  </span>
                  <span className="quality-issue-row__body">
                    <span className="quality-issue-row__top">
                      <MarketplaceMark code={issue.marketplaceCode} decorative />
                      <SeverityBadge severity={issue.severity} />
                      <EvidenceBadge
                        compact
                        kind={issue.evidence.kind}
                        source={issue.evidence.source}
                      />
                    </span>
                    <strong>{issue.title || issue.sellerSku}</strong>
                    <small>
                      {issue.sellerSku}
                      {issue.asin ? ` · ${issue.asin}` : ""}
                    </small>
                    <p>{issue.message}</p>
                  </span>
                  <ChevronRight size={17} />
                </button>
              ))}
              {response?.nextCursor && (
                <Button
                  className="quality-load-more"
                  variant="ghost"
                  loading={loadingMore}
                  onClick={() => void onLoadMore()}
                >
                  Weitere Aufgaben laden
                </Button>
              )}
            </div>
            {selected && (
              <IssueDetailPanel issue={selected} onAction={onAction} notify={notify} onConfirmed={onRetry} />
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}

const CONFIDENCE_LABELS: Record<string, string> = {
  high: "gesichert",
  medium: "wahrscheinlich",
  low: "unbestätigt",
};

const FIXABILITY_LABELS: Record<string, string> = {
  API_ATTRIBUTE: "Über den Manager behebbar",
  API_PRICE: "Preis über den Manager korrigierbar",
  API_IMAGE: "Über das Media Studio behebbar",
  REFRESH: "Nur erneut prüfen",
  SELLER_CENTRAL: "Nur in Seller Central lösbar",
  SUPPORT_CASE: "Amazon-Support erforderlich",
};

function IssueDetailPanel({
  issue,
  onAction,
  notify,
  onConfirmed,
}: {
  issue: ContentQualityIssue;
  onAction: (issue: ContentQualityIssue) => Promise<void>;
  notify: PageContext["notify"];
  onConfirmed: () => Promise<void>;
}) {
  const [working, setWorking] = useState(false);
  const [fixOpen, setFixOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const suppressed =
    issue.category === "suppression" || issue.listingStatus === "suppressed";
  const playbook = issue.playbook;
  const guidance = issue.fieldGuidance;
  const headline = guidance?.title || playbook?.title || issue.message;
  const cause = playbook?.cause || issue.explanation;
  const priceIssue = issue.fixAction.kind === "change_price";
  const repricing = issue.repricing;
  const repricerLabel = repricing?.status === "unknown" || !repricing
    ? "Status nicht prüfbar"
    : repricing.linked === false ? "Artikel nicht angebunden"
      : repricing.active ? "Angebunden und aktiv" : "Artikel angebunden · in diesem Land inaktiv";
  const priceNextStep = !repricing || repricing.status === "unknown"
    ? "Repricer-Verbindung prüfen. Erst nach erfolgreicher Abfrage lässt sich die Anbindung beurteilen."
    : repricing.linked === false
      ? `Empfohlen: Artikel ${issue.sellerSku} im Amazon-Repricer anbinden und Repricing für ${issue.marketplaceCode} aktivieren.`
      : !repricing.active
        ? `Empfohlen: Repricing für ${issue.sellerSku} in ${issue.marketplaceCode} im Repricer aktivieren.`
        : "Repricer ist aktiv. Dort Preisgrenzen und die letzte Preisübermittlung prüfen; bei weiterer Blockierung Amazons Preisgrenzen in Seller Central kontrollieren.";
  const steps = priceIssue
    ? [priceNextStep]
    : guidance ? [guidance.nextStep]
      : playbook?.steps?.length ? playbook.steps : [issue.recommendation].filter(Boolean);
  const confirmPriceAction = async () => {
    setWorking(true);
    try {
      const result = await api.confirmIssueAction(issue.id);
      notify(`Du bist fertig. Automatische Nachprüfung ab ${formatDate(result.dueAt)}.`, "success");
      await onConfirmed();
    } catch (error) {
      notify(getApiError(error), "error");
    } finally {
      setWorking(false);
    }
  };
  const fixableFields = guidance?.fields.map(field => field.name) ?? issue.affectedFields;

  useEffect(() => {
    setWorking(false);
    setFixOpen(false);
    setSupportOpen(false);
  }, [issue.id]);

  const runAction = async () => {
    if (issue.fixAction.kind === "none") return;
    if (issue.fixAction.kind === "open_media_studio") {
      await onAction(issue);
      return;
    }
    setWorking(true);
    try {
      await onAction(issue);
    } finally {
      setWorking(false);
    }
  };

  return (
    <article className="quality-issue-detail">
      <header>
        <div className="quality-issue-detail__status">
          <MarketplaceMark code={issue.marketplaceCode} />
          <StatusPill status={issue.listingStatus} />
          <SeverityBadge severity={issue.severity} />
          <EvidenceBadge kind={issue.evidence.kind} source={issue.evidence.source} />
        </div>
        <h2><small>Was ist das Problem?</small>{headline}</h2>
        <details className="quality-issue-detail__why"><summary>Ursache und Einordnung</summary>
          <strong>Was dahinter steckt</strong>
          <p>{cause}</p>
          {playbook?.impact && (
            <p className="quality-impact">
              <AlertTriangle size={14} /> {playbook.impact}
            </p>
          )}
          {playbook && (
            <p className="quality-knowledge-note">
              <Info size={13} /> {playbook.knowledgeLabel} · Einschätzung{" "}
              {CONFIDENCE_LABELS[playbook.confidence] ?? playbook.confidence}
              {playbook.matchedCode ? ` · erkannt über Code ${playbook.matchedCode}` : ""}
            </p>
          )}
        </details>
      </header>


      {guidance && <section className="issue-field-guidance" aria-label="Konkrete Beanstandung">
        <div className="guided-fix-original"><strong>Amazon beanstandet</strong><p>{guidance.message}</p></div>
        {guidance.fieldsKnown && <>
          <dl>{guidance.fields.map(field => <div key={field.name}>
            <dt>{field.label}</dt>
            <dd>{field.currentValueKnown ? formatAttributeValue(field.currentValue) : "Kein Wert im letzten Abgleich geliefert"}</dd>
          </div>)}</dl>
          <p className="field__hint">Werte aus dem letzten Abgleich. Sie können vom beanstandeten Wert abweichen. Beim Öffnen der Korrektur lädt der Manager den aktuellen Amazon-Stand und die Feldvorgaben.</p>
        </>}
      </section>}

      {steps.length > 0 && (
        <section className="quality-steps">
          <h3>
            <ListOrdered size={17} /> Was zu tun ist
            {playbook && (
              <span className={`quality-fix-badge quality-fix-badge--${playbook.fixability}`}>
                {guidance ? (guidance.fieldsKnown ? "Feldkorrektur prüfen" : "Ursache klären") : FIXABILITY_LABELS[playbook.fixability] ?? playbook.fixability}
              </span>
            )}
          </h3>
          <ol>
            {steps.slice(0, 1).map((step, index) => (
              <li key={`${issue.id}-step-${index}`}>{step}</li>
            ))}
          </ol>
          {steps.length > 1 && <details className="task-technical"><summary>Weitere Schritte ({steps.length - 1})</summary><ol start={2}>{steps.slice(1).map((step, index) => <li key={index}>{step}</li>)}</ol></details>}
          {!guidance?.fieldsKnown && playbook?.sellerCentralPath && (
            <p className="quality-steps__path">
              Weg in Seller Central: <span>{playbook.sellerCentralPath}</span>
            </p>
          )}
        </section>
      )}

      {priceIssue && (
        <section className="quality-steps" aria-label="Repricer und Wiedervorlage">
          <h3>Amazon-Repricer · {issue.marketplaceCode}</h3>
          <strong>{repricerLabel}</strong>

          {issue.followup && <p role="status"><strong>{issue.followup.message}</strong>
            {issue.followup.state === "waiting" && ` Prüfung fällig seit ${formatDate(issue.followup.dueAt)}.`}
            {issue.followup.checkedAt && ` Letzte Prüfung: ${formatDate(issue.followup.checkedAt)}.`}
          </p>}
          {issue.repricerUrl && <a className="button button--primary" href={issue.repricerUrl} target="_blank" rel="noopener noreferrer">Amazon-Repricer öffnen</a>}
          <p>Nach Durchführung bestätigen. Die automatische Nachprüfung folgt nach 24 Stunden.</p>
          <Button variant="secondary" loading={working}
            disabled={issue.followup?.state === "waiting" || issue.followup?.state === "check_failed"}
            onClick={() => void confirmPriceAction()}>
            Maßnahme durchgeführt · in 24 Stunden prüfen
          </Button>
        </section>
      )}


      <footer>
        {!priceIssue && <div className="quality-detail-actions">
          {playbook?.fixability === "API_ATTRIBUTE" && fixableFields.length > 0 ? (
            <Button icon={<Wrench size={16} />} onClick={() => setFixOpen(true)}>Feldwerte und zulässige Angaben prüfen</Button>
          ) : playbook?.fixability === "SUPPORT_CASE" && playbook.supportCaseText ? (
            <Button icon={<ClipboardCopy size={15} />} onClick={() => setSupportOpen(true)}>Support-Fall vorbereiten</Button>
          ) : (
          <Button
            loading={working}
            disabled={issue.fixAction.kind === "none"}
            onClick={() => void runAction()}
            icon={
              issue.fixAction.kind === "open_seller_central" ? (
                <ExternalLink size={16} />
              ) : issue.fixAction.kind === "refresh_listing" ? (
                <RefreshCw size={16} />
              ) : (
                <WandSparkles size={16} />
              )
            }
          >
            {issue.fixAction.label || "Keine direkte Aktion verfügbar"}
            {issue.fixAction.kind === "open_seller_central"
              ? ` (${issue.marketplaceCode})`
              : ""}
          </Button>
          )}
        </div>}
        <details className="task-technical"><summary>Weitere Aktionen</summary>
          <div className="quality-detail-actions">
            {(priceIssue || playbook?.fixability === "API_ATTRIBUTE" || playbook?.fixability === "SUPPORT_CASE") && (
          <Button
            variant="secondary"
            loading={working}
            disabled={issue.fixAction.kind === "none"}
            onClick={() => void runAction()}
            icon={
              issue.fixAction.kind === "open_seller_central" ? (
                <ExternalLink size={16} />
              ) : issue.fixAction.kind === "refresh_listing" ? (
                <RefreshCw size={16} />
              ) : (
                <WandSparkles size={16} />
              )
            }
          >
            {issue.fixAction.label || "Keine direkte Aktion verfügbar"}
            {issue.fixAction.kind === "open_seller_central"
              ? ` (${issue.marketplaceCode})`
              : ""}
          </Button>
            )}
            {playbook?.supportCaseText && playbook.fixability !== "SUPPORT_CASE" && (
              <Button variant="secondary" icon={<ClipboardCopy size={15} />} onClick={() => setSupportOpen(true)}>Support-Fall vorbereiten</Button>
            )}
            {issue.fixAction.kind !== "open_seller_central" && <Button variant="secondary" icon={<ExternalLink size={15} />} onClick={() => window.open(sellerCentralInventoryUrl(issue.marketplaceCode, issue), "_blank", "noopener,noreferrer")}>Artikel in Seller Central ({issue.marketplaceCode})</Button>}
          </div>
        </details>
        <p><ShieldCheck size={15} /> Nach der Übermittlung wird die Änderung bei Amazon überprüft.</p>
      </footer>

      <details className="task-technical task-technical--evidence"><summary>Amazon-Meldung, Artikeldaten und weitere Details</summary>
      <section className="quality-amazon-original">
        <h3>Amazon-Originalmeldung</h3>
        <p>{issue.message}</p>
        <span className="quality-amazon-original__meta">
          Code {issue.code ?? "nicht geliefert"} · Quelle{" "}
          {issue.evidence.label || issue.evidence.source}
        </span>
      </section>

      {suppressed && (
        <div className="suppression-alert">
          <Ban size={19} />
          <div>
            <strong>Amazon führt dieses Angebot als unterdrückt</strong>
            <p>
              {issue.code
                ? `Gemeldeter Code: ${issue.code}`
                : "Ein konkreter Fehlercode wurde über diese Schnittstelle nicht mitgeliefert. Die Oberfläche leitet deshalb keine Ursache ab."}
            </p>
          </div>
        </div>
      )}

      <dl className="quality-facts">
        <div>
          <dt>SKU</dt>
          <dd>{issue.sellerSku}</dd>
        </div>
        <div>
          <dt>ASIN</dt>
          <dd>{issue.asin ?? "–"}</dd>
        </div>
        <div>
          <dt>Marketplace</dt>
          <dd>{MARKETPLACE_META[issue.marketplaceCode].label} ({issue.marketplaceCode})</dd>
        </div>
        <div>
          <dt>Aktueller Amazon-Preis</dt>
          <dd>
            {typeof issue.currentPrice === "number"
              ? formatMoney(issue.currentPrice, issue.currency ?? "EUR")
              : "Nicht geliefert"}
          </dd>
        </div>
        <div>
          <dt>Amazon-Repricer</dt>
          <dd>
            {issue.repricing?.status === "active"
              ? "Aktiv · Manager-Fix gesperrt"
              : issue.repricing?.status === "inactive"
                ? "Inaktiv · Manager-Fix erlaubt"
                : "Unbekannt · sicher gesperrt"}
          </dd>
        </div>
        <div>
          <dt>Zuletzt bestätigt</dt>
          <dd>{formatDate(issue.lastConfirmedAt)}</dd>
        </div>
        <div>
          <dt>Quelle</dt>
          <dd>{issue.evidence.label || issue.evidence.source}</dd>
        </div>
        <div>
          <dt>Issue-Code</dt>
          <dd>{issue.code ?? "Nicht geliefert"}</dd>
        </div>
      </dl>

      {issue.affectedFields.length > 0 && (
        <section className="quality-detail-section">
          <h3>Betroffene Felder</h3>
          <div className="quality-field-tags">
            {issue.affectedFields.map((field) => (
              <code key={field}>{field}</code>
            ))}
          </div>
        </section>
      )}

      {issue.affectedMedia.length > 0 && (
        <section className="quality-detail-section">
          <h3>Betroffene Bilder</h3>
          <div className="quality-affected-media">
            {issue.affectedMedia.map((media, index) => (
              <div key={`${media.assetId ?? media.url ?? "media"}-${index}`}>
                <span>
                  {media.url ? (
                    <img src={media.url} alt="" loading="lazy" />
                  ) : (
                    <ImageOff size={19} />
                  )}
                </span>
                <strong>{media.role ?? `Bild ${index + 1}`}</strong>
                <small>{media.assetId ? `Asset ${media.assetId.slice(0, 8)}` : "Live-Bild"}</small>
              </div>
            ))}
          </div>
        </section>
      )}

      {issue.enforcementActions.length > 0 && (
        <section className="quality-detail-section">
          <h3>Amazon-Maßnahmen</h3>
          <ul className="quality-enforcement-list">
            {issue.enforcementActions.map((action) => (
              <li key={action}><AlertTriangle size={14} /> {action}</li>
            ))}
          </ul>
        </section>
      )}

      {(issue.exemptionActive ??
        (Boolean(issue.exemptionStatus) &&
          ["EXEMPT", "EXEMPT_UNTIL_EXPIRY_DATE"].includes(
            String(issue.exemptionStatus).toUpperCase(),
          ))) && (
          <section className="quality-enforcement-exemption">
            <ShieldCheck size={17} />
            <div>
              <strong>Amazon-Ausnahme ist aktiv</strong>
              <p>
                Die gemeldete Maßnahme wird derzeit nicht angewendet
                {issue.exemptionExpiry
                  ? ` · Ausnahme gültig bis ${formatDate(issue.exemptionExpiry)}`
                  : ""}.
              </p>
            </div>
          </section>
        )}

      <section className="quality-recommendation">
        <Lightbulb size={18} />
        <div>
          <strong>Kurzfassung</strong>
          <p>{priceIssue ? priceNextStep : guidance?.nextStep || issue.recommendation}</p>
        </div>
      </section>



      </details>

      <AttributeFixDialog
        open={fixOpen}
        issue={issue}
        onClose={() => setFixOpen(false)}
        notify={notify}
      />

      <SupportCaseDialog
        open={supportOpen}
        text={playbook?.supportCaseText ?? ""}
        onClose={() => setSupportOpen(false)}
        notify={notify}
      />
    </article>
  );
}

function SupportCaseDialog({
  open,
  text,
  onClose,
  notify,
}: {
  open: boolean;
  text: string;
  onClose: () => void;
  notify: PageContext["notify"];
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      notify("Support-Text wurde kopiert.", "success");
    } catch {
      notify("Kopieren wurde vom Browser blockiert. Bitte den Text manuell markieren.", "error");
    }
  };

  return (
    <Modal
      open={open}
      title="Support-Fall vorbereiten"
      description="Vorformulierter Text für die Amazon-Verkäuferunterstützung. Bitte vor dem Senden prüfen und Anhänge ergänzen."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Schließen
          </Button>
          <Button icon={<ClipboardCopy size={15} />} onClick={() => void copy()}>
            Text kopieren
          </Button>
        </>
      }
    >
      <pre className="support-case-text">{text}</pre>
    </Modal>
  );
}

function ImageQualityOverview({
  response,
  loading,
  error,
  loadingMore,
  onRetry,
  onLoadMore,
  onOpenStudio,
}: {
  response: ContentQualityResponse | null;
  loading: boolean;
  error: string | null;
  loadingMore: boolean;
  onRetry: () => Promise<void>;
  onLoadMore: () => Promise<void>;
  onOpenStudio: (issue: ContentQualityIssue) => Promise<void>;
}) {
  const issues = response?.items ?? [];
  const mediaIssues = useMemo(
    () =>
      issues
        .filter((issue) => issue.category === "media" || issue.affectedMedia.length > 0)
        .sort(
          (left, right) =>
            SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity] ||
            String(right.lastConfirmedAt ?? "").localeCompare(
              String(left.lastConfirmedAt ?? ""),
            ),
        ),
    [issues],
  );

  return (
    <div className="image-quality-page">
      <Panel className="image-quality-intro">
        <div>
          <span><BarChart3 size={21} /></span>
          <div>
            <p className="eyebrow">Nachvollziehbare Bildprüfung</p>
            <h2>Technische Fehler und Verkaufshinweise</h2>
            <p>
              Messbare Eigenschaften werden getrennt von Heuristiken wie Weißraum,
              Unschärfe oder möglichem Text im Bild ausgewiesen.
            </p>
          </div>
        </div>
        <div className="image-quality-intro__legend">
          <EvidenceBadge kind="amazon_confirmed" source="Amazon" />
          <EvidenceBadge kind="local_measured" source="Messung" />
          <EvidenceBadge kind="inferred" source="Einschätzung" />
        </div>
      </Panel>

      {error && !issues.length ? (
        <InlineError message={error} onRetry={() => void onRetry()} />
      ) : loading && !issues.length ? (
        <StatePanel
          type="loading"
          title="Bildprüfung wird geladen"
          description="Die vorhandenen Befunde werden nach Dringlichkeit geordnet."
        />
      ) : mediaIssues.length === 0 ? (
        <Panel>
          <StatePanel
            type="empty"
            title="Keine offenen Bildprobleme"
            description="Aktuell liegen keine Bildbefunde vor. Ein neuer Scan bleibt rein lesend."
          />
        </Panel>
      ) : (
        <div className="image-quality-grid">
          {mediaIssues.map((issue) => {
            const imageUrl = issue.affectedMedia[0]?.url ?? issue.imageUrl;
            return (
              <article className="image-quality-card" key={issue.id}>
                <div className="image-quality-card__visual">
                  {imageUrl ? (
                    <img src={imageUrl} alt="" loading="lazy" />
                  ) : (
                    <ImageOff size={27} />
                  )}
                  <MarketplaceMark code={issue.marketplaceCode} decorative />
                </div>
                <div className="image-quality-card__body">
                  <div className="image-quality-card__badges">
                    <SeverityBadge severity={issue.severity} />
                    <EvidenceBadge
                      compact
                      kind={issue.evidence.kind}
                      source={issue.evidence.source}
                    />
                  </div>
                  <h3>{issue.title || issue.sellerSku}</h3>
                  <small>
                    {issue.sellerSku}
                    {issue.code ? ` · ${issue.code}` : ""}
                  </small>
                  <strong>{issue.message}</strong>
                  <p>{issue.recommendation}</p>
                </div>
                <footer>
                  <span>
                    {issue.affectedMedia.length || 1} Bild
                    {(issue.affectedMedia.length || 1) === 1 ? "" : "er"} betroffen
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => void onOpenStudio(issue)}
                    icon={<WandSparkles size={15} />}
                  >
                    Im Studio prüfen
                  </Button>
                </footer>
              </article>
            );
          })}
        </div>
      )}
      {response?.nextCursor && (
        <div className="image-quality-load-more">
          <Button
            variant="secondary"
            loading={loadingMore}
            onClick={() => void onLoadMore()}
          >
            Weitere Bildprobleme laden
          </Button>
        </div>
      )}
    </div>
  );
}

function LockedExperimentLab({
  capabilities,
  loading,
  unavailable,
}: {
  capabilities: BrandLabCapabilities | null;
  loading: boolean;
  unavailable: boolean;
}) {
  const providedRequirements = (
    capabilities?.requirements ??
    capabilities?.prerequisites ??
    []
  ).map((requirement) =>
    typeof requirement === "string"
      ? { label: requirement, met: false, detail: "" }
      : {
          label: requirement.label,
          met: Boolean(requirement.met ?? requirement.complete),
          detail: requirement.detail ?? "",
        },
  );
  const requirements =
    providedRequirements.length > 0
      ? providedRequirements
      : [
          {
            label: "Playzeug-Markenfreigabe bestätigt",
            met: Boolean(
              capabilities?.brandApproved ||
              capabilities?.brandRegistered ||
              capabilities?.brandRegistryApproved,
            ),
            detail: "Aktuell ausstehend",
          },
          {
            label: "Amazon-Experimentberechtigung geprüft",
            met: Boolean(
              capabilities?.experimentsEnabled || capabilities?.experimentsEligible,
            ),
            detail: "Noch nicht freigeschaltet",
          },
          {
            label: "Geeignete Bundle-ASINs identifiziert",
            met: false,
            detail: "Erst nach Markenfreigabe",
          },
          {
            label: "Ergebnis- und Auditprozess aktiviert",
            met: false,
            detail: "Bleibt bis zum Produktivstart gesperrt",
          },
        ];

  return (
    <section className="locked-experiment-lab" aria-labelledby="experiment-title">
      <div className="locked-experiment-lab__watermark" aria-hidden>
        <FlaskConical size={80} />
      </div>
      <header>
        <span><LockKeyhole size={23} /></span>
        <div>
          <p className="eyebrow">Playzeug Brand Lab</p>
          <h2 id="experiment-title">Bundle- & Content-Experimente</h2>
          <p>
            Die Struktur ist vorbereitet. Start, Auswertung und Amazon-Übermittlung
            bleiben bis zur bestätigten Marken- und Programmberechtigung vollständig
            gesperrt.
          </p>
        </div>
        <span className="locked-experiment-lab__status">Gesperrt</span>
      </header>

      <div className="experiment-preview-grid" aria-label="Geplante Experimente">
        <article aria-disabled="true">
          <Images size={23} />
          <strong>Hauptbild A/B</strong>
          <p>Bildvarianten kontrolliert vergleichen.</p>
          <span><LockKeyhole size={13} /> Nicht aktiv</span>
        </article>
        <article aria-disabled="true">
          <FlaskConical size={23} />
          <strong>Bundle-Hypothesen</strong>
          <p>Set- und Polybag-Kombinationen vorbereiten.</p>
          <span><LockKeyhole size={13} /> Nicht aktiv</span>
        </article>
        <article aria-disabled="true">
          <BarChart3 size={23} />
          <strong>Content-Varianten</strong>
          <p>Titel und Marken-Content als Entwurf vergleichen.</p>
          <span><LockKeyhole size={13} /> Nicht aktiv</span>
        </article>
      </div>

      <Panel className="experiment-requirements">
        <div>
          <p className="eyebrow">Voraussetzungen</p>
          <h3>Freischaltung bleibt fail-closed</h3>
          <p>
            {loading
              ? "Berechtigungsstatus wird gelesen …"
              : unavailable
                ? "Der Berechtigungsstatus ist noch nicht verfügbar. Das Labor bleibt sicher gesperrt."
                : capabilities?.message ??
                  capabilities?.reason ??
                  "Erst wenn alle Voraussetzungen serverseitig bestätigt sind, kann eine spätere Aktivierung geprüft werden."}
          </p>
        </div>
        <ul>
          {requirements.map((requirement) => (
            <li className={requirement.met ? "is-met" : ""} key={requirement.label}>
              {requirement.met ? <Check size={15} /> : <LockKeyhole size={14} />}
              <span>
                <strong>{requirement.label}</strong>
                {requirement.detail && <small>{requirement.detail}</small>}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </section>
  );
}

function AssetQualitySummary({ asset }: { asset: StudioAsset }) {
  const hasQualityData =
    typeof asset.qualityScore === "number" ||
    Boolean(asset.qualityVerdict) ||
    Boolean(asset.qualityIssues?.length) ||
    Boolean(asset.qualityMetrics);
  const issues = normalizeAssetQualityIssues(asset.qualityIssues);
  const metrics = normalizeAssetQualityMetrics(asset.qualityMetrics).slice(0, 4);

  if (!hasQualityData) {
    return (
      <div className="asset-quality-summary asset-quality-summary--unknown">
        <Info size={16} />
        <div>
          <strong>Noch keine lokale Bildprüfung</strong>
          <p>Der Bildstatus stammt nur aus dem Medien-Workflow.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`asset-quality-summary asset-quality-summary--${asset.qualityVerdict ?? "unknown"}`}>
      <div className="asset-quality-summary__heading">
        <div>
          <span>Lokale Bildqualität</span>
          <strong>{qualityVerdictLabel(asset.qualityVerdict)}</strong>
        </div>
        {typeof asset.qualityScore === "number" && (
          <span className="asset-quality-score">
            <strong>{Math.round(asset.qualityScore)}</strong>
            <small>/100</small>
          </span>
        )}
      </div>
      {metrics.length > 0 && (
        <dl>
          {metrics.map((metric, index) => (
            <div key={metric.key ?? metric.label ?? index}>
              <dt>{metric.label ?? metric.key ?? `Messwert ${index + 1}`}</dt>
              <dd>{formatMetricValue(metric)}</dd>
            </div>
          ))}
        </dl>
      )}
      {issues.length > 0 && (
        <ul>
          {issues.slice(0, 3).map((issue, index) => (
            <li key={`${issue.code ?? "quality"}-${index}`}>
              <AlertTriangle size={13} />
              {issue.message}
            </li>
          ))}
        </ul>
      )}
      <small>
        <Info size={12} />
        Interne Messung · geprüft {formatDate(asset.qualityCheckedAt)}
      </small>
    </div>
  );
}

function EvidenceBadge({
  kind,
  source,
  compact = false,
}: {
  kind: QualityEvidenceKind;
  source: string;
  compact?: boolean;
}) {
  const amazon = isAmazonEvidence(kind);
  const label = amazon
    ? "Amazon bestätigt"
    : kind === "local_measured"
      ? "Lokal gemessen"
      : "Lokale Einschätzung";
  return (
    <span
      className={`evidence-badge evidence-badge--${amazon ? "amazon" : "local"} ${
        compact ? "evidence-badge--compact" : ""
      }`}
      title={`${label} · Quelle: ${source}`}
    >
      {amazon ? <ShieldCheck size={12} /> : <Info size={12} />}
      {label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: QualitySeverity }) {
  return (
    <span className={`severity-badge severity-badge--${severity}`}>
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

function isAmazonEvidence(kind: QualityEvidenceKind): boolean {
  return kind === "amazon_confirmed" || kind === "amazon_live_state";
}

function methodologyText(methodology: ContentQualityMethodology | undefined): string | null {
  if (typeof methodology === "string") return methodology.trim() || null;
  if (Array.isArray(methodology)) {
    const values = methodology.filter((value): value is string => typeof value === "string");
    return values.length ? values.join(" ") : null;
  }
  if (methodology && typeof methodology === "object") {
    for (const key of ["summary", "description", "note", "message"]) {
      const value = methodology[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
    const parts = ["amazon", "local", "limitation"]
      .map((key) => methodology[key])
      .filter((value): value is string => typeof value === "string" && Boolean(value.trim()))
      .map((value) => value.trim());
    if (parts.length) return parts.join(" ");
  }
  return null;
}

function normalizeAssetQualityIssues(
  qualityIssues: StudioAsset["qualityIssues"],
): MediaQualityIssue[] {
  if (!Array.isArray(qualityIssues)) return [];
  return qualityIssues.map((issue) =>
    typeof issue === "string" ? { message: issue } : issue,
  );
}

function normalizeAssetQualityMetrics(
  qualityMetrics: StudioAsset["qualityMetrics"],
): MediaQualityMetric[] {
  if (Array.isArray(qualityMetrics)) return qualityMetrics;
  if (!qualityMetrics || typeof qualityMetrics !== "object") return [];
  return Object.entries(qualityMetrics).map(([key, value]) => ({
    key,
    label: key,
    value:
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null
        ? value
        : "–",
  }));
}

function formatMetricValue(metric: MediaQualityMetric): string {
  if (metric.value === null || metric.value === undefined) return "–";
  const value =
    typeof metric.value === "boolean" ? (metric.value ? "Ja" : "Nein") : String(metric.value);
  return metric.unit ? `${value} ${metric.unit}` : value;
}

function qualityVerdictLabel(verdict: string | undefined): string {
  const normalized = String(verdict ?? "").toLowerCase();
  if (["good", "pass", "healthy"].includes(normalized)) return "Gut";
  if (["poor", "bad", "fail", "critical"].includes(normalized)) return "Kritisch";
  if (["review", "warning", "check"].includes(normalized)) return "Prüfen";
  return "Nicht bewertet";
}

function isMarketplaceCode(value: string | null): value is MarketplaceCode {
  return Boolean(value && MARKETPLACE_CODES.includes(value as MarketplaceCode));
}

function readString(record: Record<string, unknown> | null, key: string): string | undefined {
  if (!record) return undefined;
  const value = record[key];
  return typeof value === "string" ? value : undefined;
}

function normalizeMediaAssets(mediaAssets: MediaAsset[] | undefined): StudioAsset[] {
  if (!Array.isArray(mediaAssets)) return [];
  return mediaAssets
    .map((asset, index) => ({
      ...asset,
      id: asset.id || asset.assetId || `persisted-media-${index}`,
      assetId: asset.assetId || asset.id,
      state: asset.state || "prepared",
      order: typeof asset.order === "number" ? asset.order : index,
      role: asset.role || (index === 0 ? "MAIN" : `PT${index}`),
    }))
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
